import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useConnectivity } from '../../hooks/useConnectivity';

// =====================================================================
// iSentinel360 v0.4.1 — GuardFooter
//
// Extracted from src/pages/GuardDashboard.tsx (the inline footer
// block that was duplicated across four pages). Same content, same
// markup, just lifted into a reusable component.
//
// Hides itself when the user is offline — the footer relies on
// i18n translation keys (t('guard.user'), t('guard.alertActive'),
// t('guard.systemOnline')) which won't resolve if the translation
// files haven't been downloaded yet. Since the user is offline,
// those keys would show as raw strings, which looks unprofessional.
// Cleaner to just hide the footer entirely.
//
// Props mirror what the inline block needed from the parent:
//   - activeStoreName: the human-readable name of the active store
//   - hasAlerts: whether the dashboard currently has visible alerts
//   - viewMode: the dashboard's current view ('alerts' | 'history' | 'faces' | 'poi')
// =====================================================================

declare const __APP_VERSION__: string;

interface GuardFooterProps {
  activeStoreName: string | null;
  hasAlerts: boolean;
  viewMode: string;
}

export default function GuardFooter({ activeStoreName, hasAlerts, viewMode }: GuardFooterProps) {
  const { t } = useTranslation();
  const { user, setActiveStoreId } = useAuth();
  const { isOnline } = useConnectivity();

  // Hide when offline — the translation keys won't resolve cleanly
  // if the locale files haven't been downloaded yet.
  if (!isOnline) return null;

  return (
    <div className="flex-shrink-0">
      <footer className="text-center text-xs text-muted-foreground py-3 border-t">
        {activeStoreName && (
          <span className="mr-2">{activeStoreName}</span>
        )}
        {/* v0.4.9.2.4c6: 'tadmin' added. TAdmin is a global employee role
            like SAdmin — auth.js returns ALL stores for them, so GuardDashboard
            shows the StoreSelectorModal on login. Without tadmin in this list
            the footer had no way BACK to the selector once a store was
            chosen, leaving them stuck on one store. */}
        {(user?.role === 'cadmin' ||
          user?.role === 'superadmin' ||
          user?.role === 'sadmin' ||
          user?.role === 'tadmin') && user?.stores && user.stores.length > 1 && (
          <button
            onClick={() => {
              // Force re-show the store selector by clearing activeStoreId
              setActiveStoreId(null);
              // The modal will appear because activeStoreId is null and condition passes
            }}
            className="text-xs text-blue-600 hover:underline ml-2"
          >
            Switch Store
          </button>
        )}
        <div className="text-xs text-muted-foreground/70">
          {t('guard.user')}: {user?.name || 'Unknown'}
        </div>
        <div>
          <span className="mr-2">
            {hasAlerts && viewMode === 'alerts' ? t('guard.alertActive') : t('guard.systemOnline')}
          </span>
        </div>
        <span className="opacity-60 inline-block -ml-2">v.{__APP_VERSION__}</span>
      </footer>
    </div>
  );
}
